import API from "../global/api";
import { listPaging } from "../global/Utils";

/**
 * Goods received from a supplier.
 *
 * Saving one writes positive movements to the stock ledger, editing adjusts
 * them and deleting reverses them - all server-side. Nothing here touches stock
 * directly; the screens just re-read the list afterwards.
 */

// The API answers a broken stock rule (a service product on a line, a delete
// that would push an item negative) with a 400 and a readable message.
const errorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

// Add Receiving
export const addReceivingRequest = () => ({ type: "ADD_RECEIVING_REQUEST" });
export const addReceivingSuccess = (data) => ({
  type: "ADD_RECEIVING_SUCCESS",
  payload: data,
});
export const addReceivingError = (msg) => ({
  type: "ADD_RECEIVING_ERROR",
  payload: msg,
});
export const addReceiving = (
  data,
  handleVisible,
  handleDangerVisible,
  onSuccess,
) => {
  return (dispatch) => {
    dispatch(addReceivingRequest());
    API.post(`/receiving/add-receiving`, data)
      .then((res) => {
        dispatch(addReceivingSuccess(res.data));
        handleVisible(res.data.message);
        if (onSuccess) onSuccess(res.data);
      })
      .catch((error) => {
        const message = errorMessage(error, "Failed to add receiving");
        dispatch(addReceivingError(message));
        handleDangerVisible(message);
      });
  };
};

// Getting Receiving List
export const getReceivingRequest = () => ({ type: "RECEIVING_LIST_REQUEST" });
export const getReceivingSuccess = (data) => ({
  type: "RECEIVING_LIST_SUCCESS",
  payload: data,
});
export const getReceivingError = (msg) => ({
  type: "RECEIVING_LIST_ERROR",
  payload: msg,
});
export const getReceiving = (page, limit, searchTerm) => {
  return (dispatch) => {
    dispatch(getReceivingRequest());
    const paging = listPaging(page, limit);
    API.get(
      `/receiving/get-receivings?page=${paging.page}&limit=${paging.limit}&search=${searchTerm || ""}`,
    )
      .then((res) => {
        dispatch(getReceivingSuccess(res.data));
      })
      .catch((error) => {
        dispatch(
          getReceivingError(errorMessage(error, "Failed to load receivings")),
        );
      });
  };
};

// The next receiving number, so the create form shows it before saving.
export const getNextReceivingNumberRequest = () => ({
  type: "NEXT_RECEIVING_NUMBER_REQUEST",
});
export const getNextReceivingNumberSuccess = (data) => ({
  type: "NEXT_RECEIVING_NUMBER_SUCCESS",
  payload: data,
});
export const getNextReceivingNumberError = (msg) => ({
  type: "NEXT_RECEIVING_NUMBER_ERROR",
  payload: msg,
});
export const getNextReceivingNumber = (userId) => {
  return (dispatch) => {
    dispatch(getNextReceivingNumberRequest());
    API.get(`/receiving/get-last-receiving${userId ? `?userId=${userId}` : ""}`)
      .then((res) => {
        dispatch(getNextReceivingNumberSuccess(res.data));
      })
      .catch((error) => {
        // Not worth blocking the form on - the server fills the number in when
        // the request leaves it blank.
        dispatch(
          getNextReceivingNumberError(errorMessage(error, "Failed to load")),
        );
      });
  };
};

// Updating Receiving
export const updateReceivingRequest = () => ({
  type: "UPDATE_RECEIVING_REQUEST",
});
export const updateReceivingSuccess = (data) => ({
  type: "UPDATE_RECEIVING_SUCCESS",
  payload: data,
});
export const updateReceivingError = (msg) => ({
  type: "UPDATE_RECEIVING_ERROR",
  payload: msg,
});
export const updateReceiving = (
  id,
  data,
  handleVisible,
  handleDangerVisible,
  navigate,
) => {
  return (dispatch) => {
    dispatch(updateReceivingRequest());
    API.put(`/receiving/update-receiving/${id}`, data)
      .then((res) => {
        dispatch(updateReceivingSuccess(res.data));
        handleVisible(res.data.message, () =>
          navigate("/main/view-receiving-list"),
        );
      })
      .catch((error) => {
        const message = errorMessage(error, "Failed to update receiving");
        dispatch(updateReceivingError(message));
        handleDangerVisible(message);
      });
  };
};

// Deleting Receiving
export const deleteReceivingRequest = () => ({
  type: "DELETE_RECEIVING_REQUEST",
});
export const deleteReceivingSuccess = (data) => ({
  type: "DELETE_RECEIVING_SUCCESS",
  payload: data,
});
export const deleteReceivingError = (msg) => ({
  type: "DELETE_RECEIVING_ERROR",
  payload: msg,
});
export const deleteReceiving = (
  id,
  handleVisible,
  handleDangerVisible,
  page,
  limit,
  searchTerm,
) => {
  return (dispatch) => {
    dispatch(deleteReceivingRequest());
    API.delete(`/receiving/delete-receiving/${id}`)
      .then((res) => {
        dispatch(deleteReceivingSuccess(res.data));
        handleVisible(res.data.message);
        dispatch(getReceiving(page, limit, searchTerm));
      })
      .catch((error) => {
        // The usual reason a delete is refused is that the goods have already
        // been sold on, so taking them back out would push the item negative.
        const message = errorMessage(error, "Failed to delete receiving");
        dispatch(deleteReceivingError(message));
        handleDangerVisible(message);
      });
  };
};
